// The case worklist (OT_0006).
//
// Every filter is applied SERVER-side and they combine — the list a coordinator
// narrows down is the list the server counted. Nothing here filters rows in the
// browser, because a client-side filter over a truncated page silently lies
// about how many cases match.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Collapse } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";

import {
  FILTER_TOOLBAR,
  IpdButton,
  IpdDataGrid,
  IpdDateField,
  IpdFormField,
  IpdTextField,
  toolbarSx,
} from "../../ipd/utils";
import { formatIstDateTime } from "../../../utils/istDateTime";
import { fetchCases, fetchSetup } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import {
  FirmnessChip,
  LifecycleChip,
  OtSelect,
  TrackingChip,
  errorMessage,
} from "../components/common";
import {
  CASE_SOURCES,
  LIFECYCLE_LABELS,
  LIFECYCLE_ORDER,
  OT_BASE,
  SCHEDULING_TYPES,
} from "../constants";

const EMPTY_FILTERS = {
  lifecycle_status: "",
  scheduling_type: "",
  case_source: "",
  operating_room_id: "",
  surgeon_id: "",
  from: "",
  to: "",
  search: "",
};

export default function OtCases() {
  const history = useHistory();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const body = await fetchCases(params);
      setRows(body.data || []);
      setCount(body.count || 0);
    } catch (err) {
      setError(errorMessage(err, "Could not load the case list"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  useEffect(() => {
    fetchSetup()
      .then((setup) => setRooms(setup.operating_rooms || []))
      .catch(() => setRooms([]));
  }, []);

  const set = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e?.target?.value ?? e ?? "" }));

  const columns = [
    { field: "case_number", headerName: "Case", width: 140 },
    { field: "patient_name", headerName: "Patient", flex: 1, minWidth: 160 },
    { field: "health_record_id", headerName: "MRN", width: 130 },
    {
      field: "procedure",
      headerName: "Procedure",
      flex: 1,
      minWidth: 170,
      sortable: false,
      valueGetter: (p) => p.row.procedures?.find((x) => x.is_primary)?.procedure_name || "—",
    },
    { field: "primary_surgeon_name", headerName: "Surgeon", width: 150 },
    { field: "scheduling_type", headerName: "Type", width: 110 },
    {
      field: "lifecycle_status",
      headerName: "Status",
      width: 130,
      sortable: false,
      renderCell: (p) => <LifecycleChip status={p.row.lifecycle_status} />,
    },
    {
      field: "firmness",
      headerName: "List",
      width: 120,
      sortable: false,
      renderCell: (p) => <FirmnessChip firmness={p.row.firmness} />,
    },
    {
      field: "tracking_status",
      headerName: "Board",
      width: 150,
      sortable: false,
      renderCell: (p) => <TrackingChip status={p.row.tracking_status} />,
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 160,
      valueGetter: (p) => formatIstDateTime(p.row.created_at),
    },
  ];

  return (
    <OtScreen
      title="OT Cases"
      description="Every surgical case, filterable by stage, type, theatre and surgeon."
      badge={count}
      loading={loading}
      error={error}
      onRetry={() => load(applied)}
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters((v) => !v)}
          >
            Filters
          </Button>
          <IpdButton
            startIcon={<AddIcon />}
            onClick={() => history.push(`${OT_BASE}/cases/new`)}
          >
            New case
          </IpdButton>
        </Box>
      }
    >
      <Collapse in={showFilters}>
        <Box sx={{ ...toolbarSx, mb: FILTER_TOOLBAR.mb }}>
          <Box sx={{ width: FILTER_TOOLBAR.filterWidthWide }}>
          <IpdFormField label="Stage">
            <OtSelect
              placeholder="All stages"
              value={filters.lifecycle_status}
              onChange={set("lifecycle_status")}
              options={LIFECYCLE_ORDER.concat(["CANCELLED", "POSTPONED"]).map((s) => ({
                value: s,
                label: LIFECYCLE_LABELS[s],
              }))}
            />
          </IpdFormField>
          </Box>

          <Box sx={{ width: FILTER_TOOLBAR.filterWidthWide }}>
          <IpdFormField label="Scheduling type">
            <OtSelect
              placeholder="All types"
              value={filters.scheduling_type}
              onChange={set("scheduling_type")}
              options={SCHEDULING_TYPES}
            />
          </IpdFormField>
          </Box>

          <Box sx={{ width: FILTER_TOOLBAR.filterWidth }}>
          <IpdFormField label="Source">
            <OtSelect
              placeholder="All sources"
              value={filters.case_source}
              onChange={set("case_source")}
              options={CASE_SOURCES}
            />
          </IpdFormField>
          </Box>

          <Box sx={{ width: FILTER_TOOLBAR.filterWidthWide }}>
          <IpdFormField label="Theatre">
            <OtSelect
              placeholder="All theatres"
              value={filters.operating_room_id}
              onChange={set("operating_room_id")}
              options={rooms.map((r) => ({ value: r.id, label: r.name }))}
            />
          </IpdFormField>
          </Box>

          <Box sx={{ width: FILTER_TOOLBAR.filterWidth }}>
          <IpdFormField label="Booked from">
            <IpdDateField value={filters.from} onChange={(iso) => setFilters((f) => ({ ...f, from: iso || "" }))} />
          </IpdFormField>
          </Box>

          <Box sx={{ width: FILTER_TOOLBAR.filterWidth }}>
          <IpdFormField label="Booked to">
            <IpdDateField value={filters.to} onChange={(iso) => setFilters((f) => ({ ...f, to: iso || "" }))} />
          </IpdFormField>
          </Box>

          <Box sx={{ minWidth: FILTER_TOOLBAR.searchMin, maxWidth: FILTER_TOOLBAR.searchMax, flex: 1 }}>
            <IpdFormField label="Search">
              <IpdTextField
                fullWidth
                placeholder="MRN, patient, case number or procedure"
                value={filters.search}
                onChange={set("search")}
              />
            </IpdFormField>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <IpdButton onClick={() => setApplied(filters)}>Apply</IpdButton>
            <Button
              size="small"
              onClick={() => {
                setFilters(EMPTY_FILTERS);
                setApplied(EMPTY_FILTERS);
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Collapse>

      <IpdDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        fillHeight
        emptyMessage="No cases match"
        emptyDescription="Adjust the filters, or create a new case."
        onRowClick={(p) => history.push(`${OT_BASE}/case/${p.row.id}`)}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />
    </OtScreen>
  );
}
